import type {CmLogger} from "@cm/log"
import {MipLevelRegion, MipLevelRegions, Region, Regions} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-descriptor"
import {Size2, Size2Like, Vector2, Vector2Like, wrap} from "@cm/math"
import {assertNever, mapTuple} from "@cm/utils"
import {AddressMode} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/basic-definitions"
import {mapRegionToMipLevel} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/region-mapping-abstraction-experiments/region-helpers"

const MERGE_POLYGONS = false

export type BasicMappingProperties = {
    sourceSize: Size2Like
    sourceAddressMode: AddressMode
    margin: number
}

export type Mapping = BasicMappingProperties & {
    mappedVertices: MappedVertices
    mappedTriangles: MappedTriangles
}

export type MappedVertex = {
    targetPosition: Vector2Like
    sourcePosition: Vector2Like
}

export type MappedVertices = MappedVertex[]

export type MappedTriangle = [number, number, number]

export type MappedTriangles = MappedTriangle[]

export class RegionMapping {
    constructor(
        mapping?: Mapping,
        private readonly logger?: CmLogger,
    ) {
        if (mapping) {
            this.setMapping(mapping)
        }
    }

    setMapping(mapping: Mapping) {
        const start = performance.now()
        this.sourceSize = {
            width: mapping.sourceSize.width,
            height: mapping.sourceSize.height,
        }
        this.sourceAddressMode = mapping.sourceAddressMode
        const polygons = this.uvPolygonsFromMappedTriangles(mapping.mappedVertices, mapping.mappedTriangles)
        if (mapping.margin > 0) {
            throw new Error("Margin is not supported yet in RegionMapping. Please use a different method to handle margins.")
            // const boundaryPolygons = clipPolygonsWithUV(polygons, [], ClipperLib.ClipType.ctUnion)
            // const boundaryGradPolygons = this.uvPolygonsFromMappedTriangles(boundaryPolygons, mapping.mappedVertices, mapping.mappedTriangles)
            // polygons = dilatePolygonWithUV(boundaryGradPolygons, mapping.margin, ClipperLib.JoinType.jtMiter)
        }
        this._mappingBoundaryTargetToSource = polygons
        let invertedPolygons = this.invertUvPolygons(polygons)
        invertedPolygons = this.applyAddressMode(invertedPolygons, this.sourceAddressMode, this.sourceSize, false)
        this._mappingBoundarySourceToTarget = invertedPolygons
        this.logger?.debug(`RegionMapping: setMapping took ${performance.now() - start}ms`)
    }

    setAxisAlignedMapping(
        args: BasicMappingProperties & {
            targetRegion: Region
            sourceRegion: Region
        },
    ) {
        if (args.targetRegion.width <= 0 || args.targetRegion.height <= 0 || args.sourceRegion.width <= 0 || args.sourceRegion.height <= 0) {
            return // no valid mapping
        }
        const mappedVertices: MappedVertices = [
            {
                targetPosition: {
                    x: args.targetRegion.x,
                    y: args.targetRegion.y,
                },
                sourcePosition: {
                    x: args.sourceRegion.x,
                    y: args.sourceRegion.y,
                },
            },
            {
                targetPosition: {
                    x: args.targetRegion.x + args.targetRegion.width,
                    y: args.targetRegion.y,
                },
                sourcePosition: {
                    x: args.sourceRegion.x + args.sourceRegion.width,
                    y: args.sourceRegion.y,
                },
            },
            {
                targetPosition: {
                    x: args.targetRegion.x,
                    y: args.targetRegion.y + args.targetRegion.height,
                },
                sourcePosition: {
                    x: args.sourceRegion.x,
                    y: args.sourceRegion.y + args.sourceRegion.height,
                },
            },
            {
                targetPosition: {
                    x: args.targetRegion.x + args.targetRegion.width,
                    y: args.targetRegion.y + args.targetRegion.height,
                },
                sourcePosition: {
                    x: args.sourceRegion.x + args.sourceRegion.width,
                    y: args.sourceRegion.y + args.sourceRegion.height,
                },
            },
        ]
        this.setMapping({
            ...args,
            mappedVertices,
            mappedTriangles: [
                [0, 1, 2],
                [2, 1, 3],
            ],
        })
    }

    setAxisAlignedOneToOneMapping(
        args: BasicMappingProperties & {
            targetOffset: Vector2Like
            sourceRegion: Region
        },
    ) {
        this.setAxisAlignedMapping({
            ...args,
            targetRegion: {
                x: args.targetOffset.x,
                y: args.targetOffset.y,
                width: args.sourceRegion.width,
                height: args.sourceRegion.height,
            },
            sourceRegion: args.sourceRegion,
        })
    }

    getSourceMipLevelRegions(args: {targetMipLevelRegionToWrite: MipLevelRegion; sourceMipLevel: number}): MipLevelRegions {
        const start = performance.now()
        const fn = MERGE_POLYGONS ? this.clipPolygonsWithUV.bind(this) : this.clipNoMergePolygonsWithUV.bind(this)
        const targetRegionToWrite = mapRegionToMipLevel(args.targetMipLevelRegionToWrite, args.targetMipLevelRegionToWrite.mipLevel, 0)
        const targetClipPolygon = this.getRegionPolygon(targetRegionToWrite)
        const clippedTargetToSource = fn(this._mappingBoundaryTargetToSource, [targetClipPolygon], ClipperLib.ClipType.ctIntersection)
        const invertedClippedTargetToSource = this.invertUvPolygons(clippedTargetToSource)
        const sourcePolygons = this.applyAddressMode(invertedClippedTargetToSource, this.sourceAddressMode, this.sourceSize, MERGE_POLYGONS)
        const sourceRegions = this.getRectangularPolygonRegions(sourcePolygons.map((polygon) => polygon.vertices))
        const sourceMipLevelRegions = sourceRegions.map(
            (sourceRegion): MipLevelRegion => ({
                ...mapRegionToMipLevel(sourceRegion, 0, args.sourceMipLevel),
                mipLevel: args.sourceMipLevel,
            }),
        )
        this.logger?.debug(`RegionMapping: getSourceMipLevelRegions took ${performance.now() - start}ms`)
        return sourceMipLevelRegions
    }

    computeTargetInvalidations(sourceRegions: Regions): Regions {
        const start = performance.now()
        const fn = MERGE_POLYGONS ? this.clipPolygonsWithUV.bind(this) : this.clipNoMergePolygonsWithUV.bind(this)
        const sourceRegionPolygons = sourceRegions.map((sourceRegion) => this.getRegionPolygon(sourceRegion))
        const s1 = performance.now()
        const clippedPolygons = fn(this._mappingBoundarySourceToTarget, sourceRegionPolygons, ClipperLib.ClipType.ctIntersection)
        this.logger?.debug(`RegionMapping: clipNoMergePolygonsWithUV took ${performance.now() - s1}ms for ${clippedPolygons.length} polygons`)
        const s2 = performance.now()
        const targetRegions = this.getRectangularPolygonRegions(
            clippedPolygons.map((polygon) => polygon.vertices.map((vertex) => ({x: vertex.u, y: vertex.v}))),
        )
        this.logger?.debug(`RegionMapping: getRectangularPolygonRegions took ${performance.now() - s2}ms for ${targetRegions.length} regions`)
        this.logger?.debug(`RegionMapping: computeTargetInvalidations took ${performance.now() - start}ms`)
        return targetRegions
    }

    get mappingBoundaryTargetToSource(): Readonly<UvPolygons> {
        return this._mappingBoundaryTargetToSource
    }

    get mappingBoundarySourceToTarget(): Readonly<UvPolygons> {
        return this._mappingBoundarySourceToTarget
    }

    private getRegionPolygon(region: Region): Polygon {
        return [
            {
                x: region.x,
                y: region.y,
            },
            {
                x: region.x + region.width,
                y: region.y,
            },
            {
                x: region.x + region.width,
                y: region.y + region.height,
            },
            {
                x: region.x,
                y: region.y + region.height,
            },
        ]
    }

    private applyAddressMode(polygons: UvPolygons, addressMode: AddressMode, size: Size2Like, allowMerging: boolean): UvPolygons {
        if (size.width <= 0 || size.height <= 0) {
            throw new Error("Invalid size for address mode application: width and height must be greater than 0.")
        }
        const fn = allowMerging ? this.clipPolygonsWithUV.bind(this) : this.clipNoMergePolygonsWithUV.bind(this)
        const regionPolygon = this.getRegionPolygon({x: 0, y: 0, width: size.width, height: size.height})
        switch (addressMode) {
            case "border":
            case "clamp": // TODO technically "clamp" should must leave a single pixel at the nearest border to work correctly; we ignore this for now
                return fn(polygons, [regionPolygon], ClipperLib.ClipType.ctIntersection)
            case "wrap": {
                // wrap positions of the polygons (we can't wrap individual vertices, but whole polygon !)
                polygons = polygons.map((polygon) => {
                    const minVertexPosition = polygon.vertices.reduce(
                        (acc, vertex) => {
                            acc.x = Math.min(acc.x, vertex.x)
                            acc.y = Math.min(acc.y, vertex.y)
                            return acc
                        },
                        {x: Infinity, y: Infinity},
                    )
                    const wrappedMinVertexPosition = new Vector2(wrap(minVertexPosition.x, size.width), wrap(minVertexPosition.y, size.height))
                    const offset = Vector2.sub(wrappedMinVertexPosition, minVertexPosition)
                    const vertices = polygon.vertices.map((vertex) => ({
                        x: vertex.x + offset.x,
                        y: vertex.y + offset.y,
                        u: vertex.u,
                        v: vertex.v,
                    }))
                    return {
                        vertices,
                        du: polygon.du,
                        dv: polygon.dv,
                    }
                })
                // duplicate for all four quadrants
                const offsets = [
                    {x: 0, y: 0},
                    {x: size.width, y: 0},
                    {x: 0, y: size.height},
                    {x: size.width, y: size.height},
                ]
                polygons = offsets.flatMap((offset) =>
                    polygons.map((polygon) => {
                        const vertices = polygon.vertices.map((vertex) => ({
                            x: vertex.x - offset.x,
                            y: vertex.y - offset.y,
                            u: vertex.u,
                            v: vertex.v,
                        }))
                        return {
                            vertices,
                            du: polygon.du,
                            dv: polygon.dv,
                        }
                    }),
                )
                return fn(polygons, [regionPolygon], ClipperLib.ClipType.ctIntersection)
            }
            case "wrap-mirrored":
                throw new Error("Wrap and wrap-mirrored address modes are not implemented yet.")
            default:
                assertNever(addressMode)
        }
    }

    private getRectangularPolygonRegions(polygons: Polygons): Regions {
        const s3 = performance.now()
        polygons = this.clipPolygons(polygons, [], ClipperLib.ClipType.ctUnion) // merge adjacent polygons
        this.logger?.debug(`RegionMapping: getRectangularPolygonRegions clipPolygons took ${performance.now() - s3}ms for ${polygons.length} polygons`)
        return polygons.flatMap((polygon) => this.getRectangularVertexRegions(polygon))
    }

    private getRectangularVertexRegions(vertices: Vertex[]): Regions {
        // TODO this is a very naive implementation, we should find a set of rectangles which cover the UV polygons without too much excess
        // TODO alternatively we could allow for flexible (non-rectangular) regions to be used for invalidations
        const min = new Vector2(Infinity, Infinity)
        const max = new Vector2(-Infinity, -Infinity)
        vertices.forEach((vertex) => {
            min.x = Math.min(min.x, vertex.x)
            min.y = Math.min(min.y, vertex.y)
            max.x = Math.max(max.x, vertex.x)
            max.y = Math.max(max.y, vertex.y)
        })
        return [
            {
                x: min.x,
                y: min.y,
                width: max.x - min.x,
                height: max.y - min.y,
            },
        ]
    }

    // private uvPolygonsFromMappedTriangles(mappedVertices: MappedVertices, mappedTriangles: MappedTriangles): UvPolygons {
    //     return mappedTriangles.map((triangle) =>
    //         mapTuple(triangle, (index) => {
    //             const vertex = mappedVertices[index]
    //             return {
    //                 x: vertex.targetPosition.x,
    //                 y: vertex.targetPosition.y,
    //                 u: vertex.sourcePosition.x,
    //                 v: vertex.sourcePosition.y,
    //             }
    //         }),
    //     )
    // }

    private computeUvGradient(vertices: [UvVertex, UvVertex, UvVertex]) {
        const v0 = vertices[0]
        const v1 = vertices[1]
        const v2 = vertices[2]
        const p0 = new Vector2(v0.x, v0.y)
        const p1 = new Vector2(v1.x, v1.y)
        const p2 = new Vector2(v2.x, v2.y)
        const t0 = new Vector2(v0.u, v0.v)
        const t1 = new Vector2(v1.u, v1.v)
        const t2 = new Vector2(v2.u, v2.v)
        // Edge vectors in position space
        const dp1 = Vector2.sub(p1, p0)
        const dp2 = Vector2.sub(p2, p0)
        // Edge vectors in texture space
        const dt1 = Vector2.sub(t1, t0)
        const dt2 = Vector2.sub(t2, t0)
        // Compute determinant of position matrix
        const det = dp1.x * dp2.y - dp2.x * dp1.y
        if (Math.abs(det) < 1e-8) {
            throw new Error("Degenerate triangle — cannot compute UV gradient.")
        }
        const invDet = 1.0 / det
        // Solve for texture gradients using Cramer's rule
        const du = new Vector2((dt1.x * dp2.y - dt2.x * dp1.y) * invDet, (dp1.x * dt2.x - dp2.x * dt1.x) * invDet)
        const dv = new Vector2((dt1.y * dp2.y - dt2.y * dp1.y) * invDet, (dp1.x * dt2.y - dp2.x * dt1.y) * invDet)
        return {du, dv}
    }

    private uvPolygonsFromMappedTriangles(mappedVertices: MappedVertices, mappedTriangles: MappedTriangles): UvPolygons {
        return mappedTriangles.map((triangle): UvPolygon => {
            const vertices = mapTuple(triangle, (index): UvVertex => {
                const vertex = mappedVertices[index]
                return {
                    x: vertex.targetPosition.x,
                    y: vertex.targetPosition.y,
                    u: vertex.sourcePosition.x,
                    v: vertex.sourcePosition.y,
                }
            })
            const {du, dv} = this.computeUvGradient(vertices)
            return {
                vertices,
                du,
                dv,
            }
        })
    }

    private invertUvPolygons(uvPolygons: UvPolygons): UvPolygons {
        return uvPolygons.map((uvPolygon) => {
            const vertices = uvPolygon.vertices.map(
                (vertex): UvVertex => ({
                    x: vertex.u,
                    y: vertex.v,
                    u: vertex.x,
                    v: vertex.y,
                }),
            )
            const {du, dv} = this.computeUvGradient(vertices as [UvVertex, UvVertex, UvVertex])
            // TODO is there a faster way to compute du/dv from the original ones ?
            // const du = new Vector2(uvPolygon.dv.y, uvPolygon.dv.x)
            // const dv = new Vector2(uvPolygon.du.y, uvPolygon.du.x)

            return {
                vertices,
                du,
                dv,
            }
        })
    }

    // Convert to plain clipper path
    private toClipperPath(polygon: Polygon): ClipperLib.Path {
        return polygon.map(({x, y}) => ({X: x, Y: y}))
    }

    // Convert from clipper path
    private fromClipperPath(path: ClipperLib.Path): Polygon {
        return path.map((pt) => ({x: pt.X, y: pt.Y}))
    }

    // Convert from clipper path back to full vertex polygon with uv interpolation
    private fromClipperPathWithUv(path: ClipperLib.Path, originals: UvPolygons): UvPolygon {
        let closestDistance = Infinity
        let closestPolygon: UvPolygon | null = null
        for (const pt of path) {
            const p = new Vector2(pt.X, pt.Y)
            for (const poly of originals) {
                for (let i = 0; i < poly.vertices.length; i++) {
                    const a = poly.vertices[i]
                    const b = poly.vertices[(i + 1) % poly.vertices.length]
                    // Check for exact match
                    if (a.x === pt.X && a.y === pt.Y) {
                        closestPolygon = poly
                        break
                    }
                    // compute distance to the line segment (a, b)
                    const pa = Vector2.sub(p, a)
                    const ba = Vector2.sub(b, a)
                    const h = Math.max(0, Math.min(1, Vector2.dot(pa, ba) / Vector2.dot(ba, ba)))
                    const distance = Vector2.distance(pa, Vector2.mul(ba, h))
                    if (distance < closestDistance) {
                        closestDistance = distance
                        closestPolygon = poly
                    }
                }
            }
        }
        if (!closestPolygon) {
            throw new Error(`No UV polygon found for path: ${JSON.stringify(path)}`)
        }
        const result: UvPolygon = {
            vertices: path.map((pt): UvVertex => {
                const v0 = closestPolygon.vertices[0]
                const deltaPos = Vector2.sub(new Vector2(pt.X, pt.Y), v0)
                return {
                    x: pt.X,
                    y: pt.Y,
                    u: v0.u + closestPolygon.du.x * deltaPos.x + closestPolygon.du.y * deltaPos.y,
                    v: v0.v + closestPolygon.dv.x * deltaPos.x + closestPolygon.dv.y * deltaPos.y,
                }
            }),
            du: closestPolygon.du,
            dv: closestPolygon.dv,
        }
        return result
    }

    // Perform boolean operation (union, intersection, etc.)
    private clipPolygonsInternal(subject: ClipperLib.Paths, clip: ClipperLib.Paths, clipType: ClipperLib.ClipType): ClipperLib.Paths {
        this.clipper.Clear()
        this.clipper.AddPaths(subject, ClipperLib.PolyType.ptSubject, true)
        this.clipper.AddPaths(clip, ClipperLib.PolyType.ptClip, true)
        const solution: ClipperLib.Paths = []
        this.clipper.Execute(clipType, solution, ClipperLib.PolyFillType.pftNonZero, ClipperLib.PolyFillType.pftNonZero)
        return solution
    }

    // Perform boolean operation (union, intersection, etc.)
    private clipPolygons(subject: Polygons, clip: Polygons, clipType: ClipperLib.ClipType): Polygons {
        const clipperSubject = subject.map((p) => this.toClipperPath(p))
        const clipperClip = clip.map((p) => this.toClipperPath(p))
        const solution = this.clipPolygonsInternal(clipperSubject, clipperClip, clipType)
        return solution.map((p) => this.fromClipperPath(p))
    }

    // Perform boolean operation (union, intersection, etc.)
    private clipPolygonsWithUV(subject: UvPolygons, clip: Polygons, clipType: ClipperLib.ClipType): UvPolygons {
        const clipperSubject = subject.map((p) => this.toClipperPath(p.vertices))
        const clipperClip = clip.map((p) => this.toClipperPath(p))
        const solution = this.clipPolygonsInternal(clipperSubject, clipperClip, clipType)
        return solution.map((p) => this.fromClipperPathWithUv(p, subject))
    }

    // Same as above, but without merging
    private clipNoMergePolygonsWithUV(subject: UvPolygons, clip: Polygons, clipType: ClipperLib.ClipType): UvPolygons {
        return subject.flatMap((s) => this.clipPolygonsWithUV([s], clip, clipType))
    }

    private clipper = new ClipperLib.Clipper()
    private sourceSize: Size2Like = new Size2(0, 0)
    private sourceAddressMode: AddressMode = "clamp"
    // TODO are these two always just swapped (xy <-> uv) ?
    private _mappingBoundaryTargetToSource: UvPolygons = [] // CW
    private _mappingBoundarySourceToTarget: UvPolygons = [] // CW
}

export type Vertex = Vector2Like

export type Polygon = Vertex[]
export type Polygons = Polygon[]

type UvVertex = {
    x: number
    y: number
    u: number
    v: number
}

// Represents a polygon with UV attributes and gradient information (assuming constant gradient across the polygon)
type UvPolygon = {
    vertices: UvVertex[]
    du: Vector2Like
    dv: Vector2Like
}

type UvPolygons = UvPolygon[]
